import { type CSSProperties, type ReactNode, useRef } from "react";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import { OFFER_VALIDITY_DAYS } from "@/lib/offerDocumentDefaults";
import { OfferDocumentRenderer } from "@/components/offer/OfferDocumentRenderer";
import type { OfferWarrantyType, TemplateType } from "@/components/offer/offerDocumentTypes";
import {
  buildOfferNumber,
  formatPlDate,
} from "@/components/offer/offerDocumentUtils";
import type {
  ClientOfferTextOverrides,
  OfferRow,
} from "@/types/mvpEstimate";
import { DEFAULT_TEMPLATE_ID, OFFER_TEMPLATES, type OfferTemplateId } from "@/types/offerTemplates";
import styles from "./ClientOfferView.module.css";

type OfferTemplateVars = CSSProperties & Record<`--${string}`, string>;

/** Ołówek przy polach contentEditable w podglądzie oferty (tylko gdy `enabled`). */
export function OfferEditableWrap({
  enabled,
  className,
  children,
}: {
  enabled: boolean;
  className?: string;
  children: ReactNode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <div
      ref={wrapRef}
      className={[styles.editableWrap, "group", className].filter(Boolean).join(" ")}
    >
      <button
        type="button"
        className={`${styles.editablePencil} no-print`}
        aria-label="Edytuj tekst"
        onPointerDownCapture={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const el = wrapRef.current?.querySelector("[contenteditable]") as HTMLElement | null;
          el?.focus();
        }}
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden />
      </button>
      {children}
    </div>
  );
}

function buildOfferTemplateVars(templateId: OfferTemplateId): OfferTemplateVars {
  const template = OFFER_TEMPLATES.find((item) => item.id === templateId);
  const fontStack = template
    ? `"${template.fontFamily}", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`
    : `"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;

  if (!template) {
    return {
      "--offer-font-family": fontStack,
      "--offer-heading-font": fontStack,
      fontFamily: fontStack,
    };
  }

  return {
    "--offer-paper": template.colors.bodyBg,
    "--offer-accent": template.colors.accentColor,
    "--offer-line": template.colors.borderColor,
    "--offer-header-bg": template.colors.headerBg,
    "--offer-header-text": template.colors.headerText,
    "--offer-total-bg": template.colors.totalBg,
    "--offer-total-text": template.colors.totalText,
    "--offer-meta": template.colors.metaText,
    "--offer-font-family": fontStack,
    "--offer-heading-font": fontStack,
    fontFamily: fontStack,
  };
}

export type ClientOfferViewProps = {
  lines: OfferRow[];
  offerTexts?: ClientOfferTextOverrides;
  offerAssumptions?: string[];
  offerDate?: Date;
  draftId?: string;
  clientName?: string;
  clientAddress?: string;
  clientPhone?: string;
  clientEmail?: string;
  companyDataLoading?: boolean;
  companyName?: string;
  companyNip?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyLogoUrl?: string;
  companyLogoPath?: string;
  templateId?: OfferTemplateId;
  templateType?: TemplateType;
  templateStyle?: string;
  offerNumber?: string;
  isPublicOfferView?: boolean;
  publicOfferToken?: string;
  publicOfferAcceptedAt?: string | null;
  publicOfferAcceptedByName?: string | null;
  publicOfferAccepting?: boolean;
  publicOfferAcceptError?: string | null;
  onPublicOfferAccept?: (acceptedByName: string) => Promise<void>;
  onBackToEdit?: () => void;
  onOfferTextsChange?: (patch: Partial<ClientOfferTextOverrides>) => void;
  onOfferAssumptionsChange?: (next: string[]) => void;
  onPaymentTermsChange?: (value: string | undefined) => void;
  onWarrantyTypeChange?: (value: string | undefined) => void;
  onExclusionsChange?: (next: string[] | undefined) => void;
  companyNameReminder?: boolean;
  showBuilderBranding?: boolean;
  shareActionSlot?: ReactNode;
  sharePanelSlot?: ReactNode;
  materialsOwnership?: string;
  materialsNotes?: string;
  paymentTerms?: string;
  warrantyType?: OfferWarrantyType | string;
  exclusions?: string[];
  warrantyNotes?: string;
  step3SectionIncludes?: Step3SectionIncludes;
};

export function ClientOfferView({
  lines,
  offerTexts,
  offerAssumptions,
  offerDate = new Date(),
  draftId,
  clientName,
  clientAddress,
  clientPhone,
  clientEmail,
  companyDataLoading = false,
  companyName,
  companyNip,
  companyAddress,
  companyPhone,
  companyEmail,
  companyLogoUrl,
  companyLogoPath,
  templateId,
  templateType = "quick",
  templateStyle,
  offerNumber,
  isPublicOfferView = false,
  publicOfferToken,
  publicOfferAcceptedAt = null,
  publicOfferAcceptedByName = null,
  publicOfferAccepting = false,
  publicOfferAcceptError = null,
  onPublicOfferAccept,
  onBackToEdit,
  onOfferTextsChange,
  onOfferAssumptionsChange,
  onPaymentTermsChange,
  onWarrantyTypeChange,
  onExclusionsChange,
  companyNameReminder,
  showBuilderBranding = true,
  shareActionSlot,
  sharePanelSlot,
  materialsOwnership,
  materialsNotes,
  paymentTerms,
  warrantyType,
  exclusions,
  warrantyNotes,
  step3SectionIncludes,
}: ClientOfferViewProps) {
  const activeTemplate = templateId ?? DEFAULT_TEMPLATE_ID;
  const activeTemplateStyle = templateStyle ?? activeTemplate;
  const offerTemplateVars = buildOfferTemplateVars(activeTemplate);

  const companyNameTrimmed = companyName?.trim() ?? "";
  const companyNipTrimmed = companyNip?.trim() ?? "";
  const companyAddressTrimmed = companyAddress?.trim() ?? "";
  const companyPhoneTrimmed = companyPhone?.trim() ?? "";
  const companyEmailTrimmed = companyEmail?.trim() ?? "";

  const brandName = companyNameTrimmed || "Wykonawca";
  const dateStr = formatPlDate(offerDate);
  const offerNo = buildOfferNumber(draftId, offerDate);

  return (
    <div className={`${styles.viewport} client-offer-print-root`} style={offerTemplateVars}>
      {(onBackToEdit || shareActionSlot) ? (
        <div className={`${styles.actions} no-print`}>
          {onBackToEdit ? (
            <button
              type="button"
              className={styles.btn}
              onClick={onBackToEdit}
            >
              ← Wróć do edycji
            </button>
          ) : null}
          {shareActionSlot}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => window.print()}
          >
            Drukuj / Zapisz PDF
          </button>
        </div>
      ) : null}

      {companyNameReminder && !onBackToEdit ? (
        <div className={`${styles.warning} no-print`} role="status">
          Twoja oferta nie ma nazwy firmy - klient zobaczy pustą sekcję wykonawcy.{" "}
          <Link to="/ustawienia">Uzupełnij dane firmy →</Link>
        </div>
      ) : null}
      {onBackToEdit && !companyDataLoading && !companyNameTrimmed ? (
        <div className={`${styles.warning} no-print`} role="status">
          ⚠️ Brak danych firmy - klient zobaczy pustą sekcję wykonawcy.{" "}
          <Link to="/ustawienia">Uzupełnij dane firmy →</Link>
        </div>
      ) : null}

      <article
        className={styles.page}
        data-template={activeTemplate}
        data-template-type={templateType}
      >
        <OfferDocumentRenderer
          offerData={{
            lines,
            offerTexts,
            offerAssumptions,
            offerDate,
            draftId,
            clientName,
            clientAddress,
            clientPhone,
            clientEmail,
            companyDataLoading,
            companyName,
            companyNip,
            companyAddress,
            companyPhone,
            companyEmail,
            companyLogoUrl,
            companyLogoPath,
            templateId: activeTemplate,
            templateType,
            templateStyle: activeTemplateStyle,
            offerNumber,
            isPublicOfferView,
            publicOfferToken,
            publicOfferAcceptedAt,
            publicOfferAcceptedByName,
            publicOfferAccepting,
            publicOfferAcceptError,
            onPublicOfferAccept,
            onOfferTextsChange,
            onOfferAssumptionsChange,
            onPaymentTermsChange,
            onWarrantyTypeChange,
            onExclusionsChange,
            materialsOwnership,
            materialsNotes,
            paymentTerms,
            warrantyType,
            exclusions,
            warrantyNotes,
            step3SectionIncludes,
          }}
          templateType={templateType}
          templateStyle={activeTemplateStyle}
        />

        <footer className={styles.docFooter}>
          <div className={styles.docFooterCol}>
            <div className={styles.docFooterHead}>{brandName}</div>
            {companyDataLoading ? (
              <p>Ładowanie danych firmy...</p>
            ) : (
              <p>
                {companyNameTrimmed ? (
                  <>
                    {companyNameTrimmed}
                    {(companyAddressTrimmed || companyNipTrimmed) ? <br /> : null}
                  </>
                ) : null}
                {companyAddressTrimmed ? (
                  <>
                    {companyAddressTrimmed}
                    {companyNipTrimmed ? <br /> : null}
                  </>
                ) : null}
                {companyNipTrimmed ? <>NIP {companyNipTrimmed}</> : null}
              </p>
            )}
          </div>
          <div className={styles.docFooterCol}>
            <div className={styles.docFooterHead}>Kontakt</div>
            {companyDataLoading ? (
              <p>Ładowanie danych firmy...</p>
            ) : (
              <p>
                {companyPhoneTrimmed ? (
                  <>
                    {companyPhoneTrimmed}
                    {companyEmailTrimmed ? <br /> : null}
                  </>
                ) : null}
                {companyEmailTrimmed ? <>{companyEmailTrimmed}</> : null}
              </p>
            )}
          </div>
          <div className={styles.docFooterCol}>
            <div className={styles.docFooterHead}>Dokument</div>
            <p>
              {offerNo}
              <br />
              {dateStr} · Ważność: {OFFER_VALIDITY_DAYS} dni
            </p>
          </div>
        </footer>

        {showBuilderBranding ? (
          <div className={styles.builderBranding}>
            Wygenerowano w Builder · builderai.pl
          </div>
        ) : null}
      </article>

      {sharePanelSlot ? (
        <div className={`${styles.sharePanel} no-print`}>{sharePanelSlot}</div>
      ) : null}
    </div>
  );
}
